import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  AssetRoutineState,
  RoutineWorkflow,
  useCurrentWorkspace,
  useFindByQuery,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import {
  RoutineModule,
  RoutineModuleButton,
  SlideAction,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate } from 'react-router-dom';

type AssignedProductListProps = {
  productId: string;
  routines: WorkspaceRoutine[];
};

export const AssignedProductList = ({
  productId,
  routines,
}: AssignedProductListProps) => {
  const currentWorkspace = useCurrentWorkspace();
  const navigate = useNavigate();
  const slideLayoutContext = useSlideLayoutSafe();

  // Fetch workflow data to detect alerts
  const { data: workflowData } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: {
      filter: {
        product: { eq: productId },
        and: {
          disabled: {
            ne: true,
          },
        },
      },
    },
    options: { enabled: !!currentWorkspace },
  });

  // Check if routine has workflows (alerts)
  const getRoutineWorkflows = useCallback(
    (id: string) => {
      if (!id || !workflowData?.items?.length) return [];
      return workflowData.items.filter(
        (item: RoutineWorkflow) =>
          item.trigger?.config?.workspaceRoutineId === id
      );
    },
    [workflowData]
  );

  const onRoutineClick = (
    routine: WorkspaceRoutine,
    module: RoutineModule<any, any>,
    _state?: AssetRoutineState<any>,
    _additionalState?: any,
    _workflows?: RoutineWorkflow[]
  ) => {
    if (!routine?.id) return;
    slideLayoutContext?.prepareSlideTransition(SlideAction.NEXT);
    navigate(`/products/${productId}/routines/${routine.id}`, {
      state: { slideLayoutInitialTransition: SlideAction.NEXT },
    });
  };

  return (
    <View style={styles.container}>
      {routines.map((routine, index) => {
        const workflows = getRoutineWorkflows(routine.id ?? '');
        const alertIcon =
          workflows.length > 0 ? (
            <MaterialCommunityIcons name="alarm-check" size={16} />
          ) : undefined;
        return (
          <RoutineModuleButton
            key={routine.id}
            routine={routine}
            buttonIndex={index}
            buttonCount={routines.length}
            workflows={workflows}
            leftButtonIcon={alertIcon}
            onClick={onRoutineClick}
          />
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    gap: 4,
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
  }
`;
